import React, { useState, useEffect } from 'react';
import { getGapAnalysen, updateGapAnalyse } from '../../../../../services/gapAnalyseService';
import { getProzesse } from '../../../../../services/schutzbedarfsanalyseService';
import api from '../../../../../services/api';
import GapAnalyseModal from './GapAnalyseModal';
import './GapAnalyseSection.css';

const GapAnalyseSection = () => {
    const [gaps, setGaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGap, setSelectedGap] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterKritikalitaet, setFilterKritikalitaet] = useState('alle');
    const [filterKategorie, setFilterKategorie] = useState('alle');
    const [filterStatus, setFilterStatus] = useState('alle');
    const [filterProzess, setFilterProzess] = useState('alle');
    const [filterAnwendung, setFilterAnwendung] = useState('alle');
    const [expandedGapId, setExpandedGapId] = useState(null);
    const [prozesse, setProzesse] = useState([]);
    const [anwendungen, setAnwendungen] = useState([]);

    useEffect(() => {
        loadGaps();
        loadProzesseAndAnwendungen();
    }, []);

    const loadGaps = async () => {
        setLoading(true);
        const result = await getGapAnalysen();
        if (result.success) {
            setGaps(result.data);
        } else {
            console.error('Fehler beim Laden der GAP-Analysen:', result.error);
        }
        setLoading(false);
    };

    const loadProzesseAndAnwendungen = async () => {
        try {
            const mandantId = JSON.parse(localStorage.getItem('selectedTenant')).mandant_id;

            const prozesseResult = await getProzesse();
            if (prozesseResult.success) {
                setProzesse(prozesseResult.data);
            }

            const anwendungenResult = await api.get('/api/schutzbedarfsanalyse/anwendungen', {
                headers: { 'X-Mandant-Id': mandantId }
            });
            if (anwendungenResult.data) {
                setAnwendungen(anwendungenResult.data);
            }
        } catch (error) {
            console.error('Fehler beim Laden der Prozesse/Anwendungen:', error);
        }
    };

    const handleEditGap = (gap) => {
        setSelectedGap(gap);
        setIsModalOpen(true);
    };

    const handleSaveGap = async (gapData) => {
        const result = await updateGapAnalyse(selectedGap.gap_id, gapData);
        if (result.success) {
            await loadGaps();
            setIsModalOpen(false);
            setSelectedGap(null);
        } else {
            alert('Fehler beim Aktualisieren: ' + result.error);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedGap(null);
    };

    const toggleGapDetails = (gapId) => {
        setExpandedGapId(expandedGapId === gapId ? null : gapId);
    };

    // Filter anwenden
    const filteredGaps = gaps.filter(gap => {
        if (filterKritikalitaet !== 'alle' && gap.kritikalitaet !== filterKritikalitaet) return false;
        if (filterKategorie !== 'alle' && gap.gap_kategorie !== filterKategorie) return false;
        if (filterStatus !== 'alle' && gap.massnahme_status !== filterStatus) return false;
        if (filterProzess !== 'alle' && gap.prozess_id !== filterProzess) return false;
        if (filterAnwendung !== 'alle' && gap.anwendung_id !== filterAnwendung) return false;
        return true;
    });

    // Statistiken
    const stats = {
        gesamt: gaps.length,
        kritisch: gaps.filter(g => g.kritikalitaet === 'kritisch').length,
        hoch: gaps.filter(g => g.kritikalitaet === 'hoch').length,
        offen: gaps.filter(g => g.massnahme_status === 'offen').length,
    };

    const getProzessName = (prozessId) => {
        if (!prozessId) return '-';
        const prozess = prozesse.find(p => p.prozess_id === prozessId);
        return prozess?.prozess_name || 'Unbekannt';
    };

    const getAnwendungName = (anwendungId) => {
        if (!anwendungId) return '-';
        const anwendung = anwendungen.find(a => a.anwendung_id === anwendungId);
        return anwendung?.anwendung_name || 'Unbekannt';
    };

    const getKritikalitaetColor = (kritikalitaet) => {
        const colors = {
            'kritisch': '#dc2626',
            'hoch': '#f59e0b',
            'mittel': '#fbbf24',
            'niedrig': '#10b981'
        };
        return colors[kritikalitaet] || '#6b7280';
    };

    const getStatusColor = (status) => {
        const colors = {
            'offen': '#9ca3af',
            'in_planung': '#6366f1',
            'in_umsetzung': '#3b82f6',
            'abgeschlossen': '#10b981',
            'zurueckgestellt': '#ef4444'
        };
        return colors[status] || '#6b7280';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('de-DE');
    };

    if (loading) {
        return (
            <div className="gap-analyse-section">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>GAP-Analysen werden geladen...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gap-analyse-section">
            {/* Header */}
            <div className="gap-analyse-header">
                <div className="header-content">
                    <h2>GAP-Analyse</h2>
                    <p className="header-subtitle">
                        Compliance-Lücken • System prüft kontinuierlich auf BaFin/DORA/BSI-Konformität
                    </p>
                </div>
                <div className="header-actions">
                    <button className="btn-refresh" onClick={loadGaps}>
                        🔄 Aktualisieren
                    </button>
                </div>
            </div>

            {/* Statistiken */}
            <div className="gap-stats">
                <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.gesamt}</div>
                        <div className="stat-label">Gesamt GAPs</div>
                    </div>
                </div>
                <div className="stat-card critical">
                    <div className="stat-icon">🔴</div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.kritisch}</div>
                        <div className="stat-label">Kritische GAPs</div>
                    </div>
                </div>
                <div className="stat-card high">
                    <div className="stat-icon">🟠</div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.hoch}</div>
                        <div className="stat-label">Hohe GAPs</div>
                    </div>
                </div>
                <div className="stat-card open">
                    <div className="stat-icon">📋</div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.offen}</div>
                        <div className="stat-label">Offen</div>
                    </div>
                </div>
            </div>

            {/* Filter */}
            <div className="gap-filters">
                <div className="filter-group">
                    <label>Kritikalität:</label>
                    <select value={filterKritikalitaet} onChange={(e) => setFilterKritikalitaet(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="kritisch">Kritisch</option>
                        <option value="hoch">Hoch</option>
                        <option value="mittel">Mittel</option>
                        <option value="niedrig">Niedrig</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Kategorie:</label>
                    <select value={filterKategorie} onChange={(e) => setFilterKategorie(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="integration">Integration</option>
                        <option value="kontroll">Kontrolle</option>
                        <option value="dokumentation">Dokumentation</option>
                        <option value="governance">Governance</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Status:</label>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="offen">Offen</option>
                        <option value="in_planung">In Planung</option>
                        <option value="in_umsetzung">In Umsetzung</option>
                        <option value="abgeschlossen">Abgeschlossen</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Prozess:</label>
                    <select value={filterProzess} onChange={(e) => setFilterProzess(e.target.value)}>
                        <option value="alle">Alle Prozesse</option>
                        {prozesse.map(p => (
                            <option key={p.prozess_id} value={p.prozess_id}>
                                {p.prozess_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Anwendung:</label>
                    <select value={filterAnwendung} onChange={(e) => setFilterAnwendung(e.target.value)}>
                        <option value="alle">Alle Anwendungen</option>
                        {anwendungen.map(a => (
                            <option key={a.anwendung_id} value={a.anwendung_id}>
                                {a.anwendung_name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* TABELLE */}
            <div className="gaps-table-container">
                <table className="gaps-table">
                    <thead>
                    <tr>
                        <th className="col-bezeichnung">BEZEICHNUNG</th>
                        <th className="col-kritikalitaet">KRITIKALITÄT</th>
                        <th className="col-status">STATUS</th>
                        <th className="col-frist">FRIST</th>
                        <th className="col-actions">AKTIONEN</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredGaps.length === 0 ? (
                        <tr>
                            <td colSpan="5" className="empty-row">
                                <div className="empty-state">
                                    <div className="empty-icon">✅</div>
                                    <h3>Keine GAPs gefunden</h3>
                                    <p>
                                        {gaps.length === 0
                                            ? 'Keine Compliance-Lücken erkannt. Alle Anforderungen erfüllt!'
                                            : 'Keine GAPs entsprechen den Filterkriterien.'
                                        }
                                    </p>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        filteredGaps.map(gap => (
                            <React.Fragment key={gap.gap_id}>
                                <tr className="gap-row" onClick={() => toggleGapDetails(gap.gap_id)}>
                                    <td className="bezeichnung-cell">
                                            <span className="expand-icon">
                                                {expandedGapId === gap.gap_id ? '▼' : '▶'}
                                            </span>
                                        <div className="gap-info">
                                            <div className="context-badges">
                                                {gap.prozess_id && (
                                                    <span className="context-badge prozess-badge">
                                                            {getProzessName(gap.prozess_id)}
                                                        </span>
                                                )}
                                                {gap.anwendung_id && (
                                                    <span className="context-badge anwendung-badge">
                                                            {getAnwendungName(gap.anwendung_id)}
                                                        </span>
                                                )}
                                            </div>
                                            <span className="gap-name">{gap.gap_titel}</span>
                                        </div>
                                    </td>
                                    <td>
                                            <span
                                                className="badge"
                                                style={{ backgroundColor: getKritikalitaetColor(gap.kritikalitaet) }}
                                            >
                                                {gap.kritikalitaet?.toUpperCase()}
                                            </span>
                                    </td>
                                    <td>
                                            <span
                                                className="badge"
                                                style={{ backgroundColor: getStatusColor(gap.massnahme_status) }}
                                            >
                                                {gap.massnahme_status?.replace('_', ' ')}
                                            </span>
                                    </td>
                                    <td>{formatDate(gap.frist)}</td>
                                    <td className="col-actions">
                                        <button
                                            className="btn-table btn-edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditGap(gap);
                                            }}
                                        >
                                            Maßnahmen
                                        </button>
                                        <button className="btn-table btn-analyze">
                                            Analysieren
                                        </button>
                                    </td>
                                </tr>

                                {expandedGapId === gap.gap_id && (
                                    <tr className="detail-row">
                                        <td colSpan="5">
                                            <div className="detail-content">
                                                <div className="detail-section">
                                                    <strong>Beschreibung:</strong>
                                                    <p>{gap.gap_beschreibung}</p>
                                                </div>

                                                <div className="detail-section">
                                                    <strong>Erkennungsgrund:</strong>
                                                    <p className="erkennungs-grund">
                                                        {gap.erkennungs_grund || 'Keine Begründung'}
                                                    </p>
                                                </div>

                                                <div className="detail-grid">
                                                    <div>
                                                        <strong>Kontrollanforderung:</strong>
                                                        <p>{gap.kontrollanforderung || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <strong>Ist-Zustand:</strong>
                                                        <p>{gap.ist_zustand || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <strong>Soll-Zustand:</strong>
                                                        <p>{gap.soll_zustand || '-'}</p>
                                                    </div>
                                                </div>

                                                {gap.massnahme_beschreibung && (
                                                    <div className="detail-section">
                                                        <strong>Maßnahmenbeschreibung:</strong>
                                                        <p>{gap.massnahme_beschreibung}</p>
                                                    </div>
                                                )}

                                                <div className="detail-footer">
                                                    <span>Erstellt: {formatDate(gap.created_at)}</span>
                                                    <span>Letzte Prüfung: {formatDate(gap.letzte_pruefung)}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && selectedGap && (
                <GapAnalyseModal
                    gap={selectedGap}
                    prozessName={getProzessName(selectedGap.prozess_id)}
                    anwendungName={getAnwendungName(selectedGap.anwendung_id)}
                    onSave={handleSaveGap}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default GapAnalyseSection;